import { Inject } from '@odinjs/odin';
import { ActionAPI, AutocompleteAPI, DeckAPI, FetchAPI, ModalService } from '@philips-internal/oswald-ui-components';
import { SelectionType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnMount, OnSelectItem, OnSubmit, OnUnselectItem } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SelectItemEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isMobileResolution, resolvePath } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { FragmentNames, MyChartDischargeSummaryAliases } from '../../../codegen';
import { YES } from '../../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import { cast } from '../../../utils/cast-helpers';
import { defaultIfUndefined } from '../../../utils/object-utils';
import SummaryContext from '../../../utils/summary-context';
import UserContext from '../../../utils/user-context';
import BaseController from '../../BaseController';
import { ReportGenerateResponse, SummaryConfig } from '../my-chart-discharge-summary-interfaces';
import { ReportGroup } from '../my-chart-discharge-summary-types';
import './my-chart-discharge-summary-controller';

interface SummaryTypeData {
  codeTypeSummary: string;
  summaryType: string;
}

interface EncounterTypeData {
  encounterType: string;
  encounterTypeSequence: string;
}

interface FilterProps {
  encounterType: EncounterTypeData;
  filterFromDate: string;
  summaryType: SummaryTypeData;
}

interface DischargeSummaryData {
  dateReport: string;
  doctorName: string;
  documentType: string;
  read: string;
  sequence: number;
  serviceNumber: number;
  summaryType: string;
}

@Controller({ fragment: FragmentNames.MY_CHART_DISCHARGE_SUMMARY })
export default class MyChartDischargeSummaryController extends BaseController {

  @Inject(MyChartDischargeSummaryAliases.FETCH_RECEIVED)
  private fetchReceivedApi!: FetchAPI;

  @Inject(MyChartDischargeSummaryAliases.BUTTON_EMAIL)
  private emailActionApi!: ActionAPI;

  @Inject(MyChartDischargeSummaryAliases.BUTTON_DOWNLOAD)
  private downloadActionApi!: ActionAPI;

  @Inject(MyChartDischargeSummaryAliases.DECK_RECEIVED)
  private deckAPI!: DeckAPI;

  @Inject(MyChartDischargeSummaryAliases.AUTOCOMPLETE_SUMMARY_TYPE)
  private autocompleteApi!: AutocompleteAPI;

  @OnMount({ target: MyChartDischargeSummaryAliases.LAYOUT_DISCHARGE_SUMMARY })
  handleLayoutDischargeSummaryOnMount(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_SUMMARY_CONFIG)
      .then(response => {
        const config = response.body as SummaryConfig;
        const showExams = config.showExams === YES;
        const showDocumentExchange = config.showDocumentExchange === YES;
        this.deckAPI.setSelectionType(this.getSelectionType(showExams, showDocumentExchange));
      });
  }

  private getSelectionType(shouldShowExams: boolean, shouldShowDocumentExchange: boolean): SelectionType {
    if (isMobileResolution() || shouldShowExams || shouldShowDocumentExchange) {
      return SelectionType.NONE
    }
    return SelectionType.MULTIPLE
  }

  @OnMount({ target: MyChartDischargeSummaryAliases.FETCH_RECEIVED })
  handleFetchDischargeSummary(event: MountEvent): void {
    const fetchAPI = event.target as FetchAPI;
    this.context.set({ personCode: UserContext.patientId });
    fetchAPI.forceFetch();
  }

  @OnAfterFetch(MyChartDischargeSummaryAliases.FETCH_RECEIVED)
  handleDataAfterFetch(event: FetchEvent): void {
    const data = defaultIfUndefined(event.data, []) as DischargeSummaryData[];
    const newData = data.map<DischargeSummaryData>(item => {
      return {
        ...item,
        dateReport: moment(new Date(item.dateReport)).format('L'),
        doctorName: item.doctorName ? item.doctorName : '-'
      }
    });

    this.deckAPI.setData(cast(defaultIfUndefined(newData, [])));
  }

  @OnAfterFetch(MyChartDischargeSummaryAliases.FETCH_SUMMARY_TYPE_LIST)
  handleAfterSummaryTypeListFetch(evt: FetchEvent): void {
    const summaryData = evt.data as SummaryTypeData[];
    const newSummaryData = summaryData.map(item => {
      return {
        ...item,
        summaryType: MessageService.translate(item.summaryType)
      }
    })
    this.autocompleteApi.setData(newSummaryData);
  }

  @OnMount({ target: MyChartDischargeSummaryAliases.DECK_RECEIVED })
  handleMountDeck(event: MountEvent): void {
    const deckAPI = event.target as DeckAPI;
    if (isMobileResolution()) {
      deckAPI.setSelectionType(SelectionType.NONE);
    }
  }

  @OnSelectItem(MyChartDischargeSummaryAliases.DECK_RECEIVED)
  handleOnSelectDeckItem(event: SelectItemEvent<DischargeSummaryData>): void {
    const deckApi = event.target as unknown as DeckAPI;
    if (deckApi.getSelectedCards().length && !isMobileResolution()) {
      this.enableContextualBarActions(true);
    }
  }

  @OnUnselectItem(MyChartDischargeSummaryAliases.DECK_RECEIVED)
  handleOnUnSelectDeckItem(event: SelectItemEvent<DischargeSummaryData>): void {
    const deckApi = event.target as unknown as DeckAPI;

    if (!deckApi.getSelectedCards().length) {
      this.enableContextualBarActions(false);
    }
  }

  private enableContextualBarActions(enable: boolean): void {
    this.emailActionApi.setEnabled(enable);
    this.downloadActionApi.setEnabled(enable);
  }

  @OnSubmit({ target: MyChartDischargeSummaryAliases.FILTER_DISCHARGE_SUMMARY })
  onSubmitFilter(event: SubmitEvent): void {
    const filterProps = event.record as FilterProps;
    const dateFilter = filterProps?.filterFromDate ? filterProps.filterFromDate : '';
    const summaryType = filterProps?.summaryType ? filterProps.summaryType.codeTypeSummary : '';
    const encounterType = filterProps?.encounterType ? filterProps.encounterType.encounterTypeSequence : '';
    const params = { dateFilter, encounterType, summaryType };

    this.context.set({ personCode: UserContext.patientId });
    this.fetchReceivedApi.setParameters(params);
    this.fetchReceivedApi.forceFetch();
  }

  @OnClick({ target: MyChartDischargeSummaryAliases.MY_CHART_DISCHARGE_SUMMARY_CARD })
  handleClickAllergieCard(event: ClickEvent): void {
    const { sequence, serviceNumber, doctorName } = event.value;
    SummaryContext.serviceNumber = serviceNumber;
    SummaryContext.doctorName = doctorName;

    HttpService.post(APPLICATION_ENDPOINTS.POST_MC_READ_DOCUMENT_MICROSSERVICE, { summarySequence: sequence }, {})
    applicationContext.redirect(APPLICATION_ROUTES.MY_CHART_DISCHARGE_SUMMARY_DETAILS, { summarySequence: sequence });
  }

  @OnClick({ target: ['button-email'] })
  onEmailBtnClick(): void {
    const contents = this.createParameterList();
    const mailParams = {
      contents: contents,
      groupCode: ReportGroup.DISCHARGE_SUMMARY,
      unifiedReport: false
    };

    ModalService.openDefault({
      closeable: true,
      contextData: mailParams,
      dockButtons: [],
      fragment: FragmentNames.SEND_MAIL_SUMMARY,
      title: 'patient.send-by-email',
    });
  }

  @OnClick({ target: ['button-download', 'menu-option-download-report'] })
  onDownloadBtnClick(event: ClickEvent): void {
    if (event) {
      this.context.set({ personCode: UserContext.patientId });
      const reportDTO = this.createParameterList();
      const body = { reportDTO };

      HttpService.post<ReportGenerateResponse>(APPLICATION_ENDPOINTS.GET_MC_SUMMARY_DETAIL_DATA_REPORT, { patientId: UserContext.patientId }, { body })
        .then(response => resolvePath('/preview?uri=:uri', { uri: response.body?.uri }))
        .then(file => this.downloadReport(file));
    }
  }

  private createParameterList(): Array<Value> {
    const parameterList = [] as Array<Value>;
    const selectedCards = this.deckAPI.getSelectedCards() as unknown as Array<DischargeSummaryData>;

    selectedCards.forEach(data => {
      if (data.sequence) {
        const params = {
          groupCode: ReportGroup.DISCHARGE_SUMMARY,
          physicianName: data.doctorName,
          serviceNumber: data.serviceNumber,
          summarySequence: data.sequence,
        };
        parameterList.push(params);
      }
    });

    return parameterList;
  }

  downloadReport(file: string): void {
    const win = window.open(file, '_blank');
    win && win.focus();
  }

}
